import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Background,
  BackgroundVariant,
  Connection,
  ConnectionMode,
  Controls,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useEdgesState,
  useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { v4 as uuid } from "uuid";

import { AtlasNode } from "../components/AtlasNode";
import type { NodeData } from "../lib/api";

const nodeTypes = { atlas: AtlasNode };
const initialNodes: Node<NodeData>[] = [
  {
    id: "a",
    type: "atlas",
    position: { x: 0, y: 0 },
    data: {
      id: "a",
      type: "gruppe",
      label: "Alpha",
      position: { x: 0, y: 0 },
      inactive: false,
      constituted: false,
      collapsed: false,
      contacts: [],
      links: [],
      notes: [],
    },
    draggable: true,
    selectable: true,
  },
  {
    id: "b",
    type: "atlas",
    position: { x: 300, y: 140 },
    data: {
      id: "b",
      type: "gruppe",
      label: "Beta",
      position: { x: 300, y: 140 },
      inactive: false,
      constituted: false,
      collapsed: false,
      contacts: [],
      links: [],
      notes: [],
    },
    draggable: true,
    selectable: true,
  },
  {
    id: "c",
    type: "atlas",
    position: { x: -160, y: -80 },
    data: {
      id: "c",
      type: "gruppe",
      label: "Gamma",
      position: { x: -160, y: -80 },
      inactive: false,
      constituted: false,
      collapsed: false,
      contacts: [],
      links: [],
      notes: [],
    },
    draggable: true,
    selectable: true,
  },
];

const initialEdges: Edge[] = [
  {
    id: "edge-a-b",
    source: "a",
    target: "b",
    data: { arrow_style: "default" },
    markerEnd: { type: "arrowclosed" },
  },
];

export const DebugGraphPage: React.FC = () => {
  const [nodes, setNodes, _onNodesChangeBase] = useNodesState<NodeData>(initialNodes);
  const [edges, setEdges, _onEdgesChangeBase] = useEdgesState(initialEdges);
  const [log, setLog] = useState<string[]>([]);
  const [history, setHistory] = useState<{ nodes: Node<NodeData>[]; edges: Edge[] }[]>([
    JSON.parse(JSON.stringify({ nodes: initialNodes, edges: initialEdges })),
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(historyIndex);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  const addLog = useCallback((msg: string) => {
    setLog((prev) => [...prev.slice(-9), `${new Date().toLocaleTimeString()}: ${msg}`]);
  }, []);

  const snapshot = useCallback(
    (n: Node<NodeData>[], e: Edge[]) => JSON.parse(JSON.stringify({ nodes: n, edges: e })),
    []
  );

  const pushSnapshot = useCallback(
    (n: Node<NodeData>[], e: Edge[], reason: string) => {
      const snap = snapshot(n, e);
      setHistory((prev) => {
        const trimmed = prev.slice(0, historyIndexRef.current + 1);
        const next = [...trimmed, snap].slice(-40);
        const idx = next.length - 1;
        historyIndexRef.current = idx;
        setHistoryIndex(idx);
        return next;
      });
      addLog(`snapshot: ${reason}`);
    },
    [addLog, snapshot]
  );

  const onNodesChange = useCallback(
    (changes) => {
      const updated = applyNodeChanges(changes, nodes);
      setNodes(updated);
      const isDragging = changes.some((c: any) => c.type === "position" && c.dragging);
      const dragEnded = changes.some((c: any) => c.type === "position" && c.dragging === false);
      const structural = changes.some((c: any) => c.type !== "select");
      if (!structural) return;
      if (dragEnded || !isDragging) {
        pushSnapshot(updated, edges, dragEnded ? "drag stop" : "nodes change");
      }
    },
    [edges, nodes, pushSnapshot, setNodes]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const updated = applyEdgeChanges(changes, edges);
      setEdges(updated);
      const structural = changes.some((c: any) => c.type !== "select");
      if (structural) {
        pushSnapshot(nodes, updated, "edges change");
      }
    },
    [edges, nodes, pushSnapshot, setEdges]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const id = uuid();
      addLog(`connect: ${connection.source} -> ${connection.target}`);
      const updatedEdges = addEdge(
        {
          ...connection,
          id,
          data: { arrow_style: "default" },
          markerEnd: { type: "arrowclosed" as const },
        },
        edges
      );
      setEdges(updatedEdges);
      pushSnapshot(nodes, updatedEdges, "connect");
    },
    [addLog, edges, nodes, pushSnapshot, setEdges]
  );

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    const nextIndex = historyIndexRef.current - 1;
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
    const snap = historyRef.current[nextIndex];
    setNodes(snap.nodes);
    setEdges(snap.edges);
    addLog("undo");
  }, [addLog, setEdges, setNodes]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    const nextIndex = historyIndexRef.current + 1;
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
    const snap = historyRef.current[nextIndex];
    setNodes(snap.nodes);
    setEdges(snap.edges);
    addLog("redo");
  }, [addLog, setEdges, setNodes]);

  const resetGraph = useCallback(() => {
    const seed = snapshot(initialNodes, initialEdges);
    setNodes(seed.nodes);
    setEdges(seed.edges);
    pushSnapshot(seed.nodes, seed.edges, "reset");
  }, [pushSnapshot, setEdges, setNodes, snapshot]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  useEffect(() => {
    addLog(`state: ${nodes.length} nodes / ${edges.length} edges`);
  }, [addLog, edges, nodes]);

  return (
    <div className="h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-4">
        <h1 className="text-xl font-bold mb-2">Debug Graph - Local History</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Drag nodes, connect handles, and use Undo/Redo to verify the offline editor path.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <button className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleUndo} disabled={!canUndo}>
            Undo
          </button>
          <button className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleRedo} disabled={!canRedo}>
            Redo
          </button>
          <button className="btn btn-secondary" onClick={resetGraph}>
            Reset
          </button>
          <span className="text-slate-500 dark:text-slate-400">
            History {historyIndex + 1}/{history.length}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            Nodes {nodes.length} / Edges {edges.length}
          </span>
        </div>
        <div className="mt-3 space-y-1 font-mono text-xs">
          {log.length === 0 && <div className="text-slate-400">No events yet...</div>}
          {log.map((entry, i) => (
            <div key={i} className="text-slate-700 dark:text-slate-300">{entry}</div>
          ))}
        </div>
      </div>
      <div className="flex-1">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            connectionMode={ConnectionMode.Loose}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={24} size={1} variant={BackgroundVariant.Dots} />
            <Controls />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
};
